/**
 * Services Index File
 * Tüm servislerin export edildiği merkezi dosya
 */

// First, import service instances for local usage
import { authService } from './auth.service';
import { alarmService } from './alarm.service';
import { unitService } from './unit.service';
import { wagonService } from './wagon.service';
import { fleetService } from './fleet.service';
import { userService } from './user.service';
import { tenantService } from './tenant.service';
import { buildingService } from './building.service';
import { notificationService } from './notification.service';

// Import all services
export { authService, AuthService } from './auth.service';
export type {IAuthService} from './auth.service';
export { alarmService, AlarmService } from './alarm.service';
export type { IAlarmService } from './alarm.service';
export { unitService, UnitService } from './unit.service';
export type { IUnitService } from './unit.service';

// Import other services (placeholder implementations)
export { wagonService, WagonService } from './wagon.service';
export type { IWagonService } from './wagon.service';
export { fleetService, FleetService } from './fleet.service';
export type { IFleetService } from './fleet.service';
export { userService, UserService } from './user.service';
export type { IUserService } from './user.service';
export { tenantService, TenantService } from './tenant.service';
export type { ITenantService } from './tenant.service';
export { buildingService, BuildingService } from './building.service';
export type { IBuildingService } from './building.service';
export { notificationService, NotificationService } from './notification.service';
export type { INotificationService } from './notification.service';

/**
 * Service Factory - Dinamik olarak servis oluşturmak için
 */
export class ServiceFactory {
  private static readonly instances: Map<string, any> = new Map();

  /**
   * Servis instance'ı getir veya oluştur
   */
  static getService<T>(ServiceClass: new () => T): T {
    const className = ServiceClass.name;
    
    if (!this.instances.has(className)) {
      this.instances.set(className, new ServiceClass());
    }
    
    return this.instances.get(className);
  }

  /**
   * Tüm servisleri temizle (testing için)
   */
  static clearAll(): void {
    this.instances.clear();
  }

  /**
   * Belirli bir servisi temizle
   */
  static clearService(ServiceClass: any): void {
    const className = ServiceClass.name;
    this.instances.delete(className);
  }
}

// ==================== SERVICE REGISTRY ====================

/**
 * Service Registry - Merkezi servis yönetimi
 */
export class ServiceRegistry {
  private static readonly registry: Map<string, any> = new Map();

  /**
   * Servis kaydet
   */
  static register(name: string, service: any): void {
    this.registry.set(name, service);
  }

  /**
   * Servis al
   */
  static get<T>(name: string): T {
    const service = this.registry.get(name);
    
    if (!service) {
      throw new Error(`Service "${name}" not found in registry`);
    }
    
    return service;
  }

  /**
   * Tüm servisleri al
   */
  static getAll(): Map<string, any> {
    return new Map(this.registry);
  }

  /**
   * Servis var mı kontrol et
   */
  static has(name: string): boolean {
    return this.registry.has(name);
  }

  /**
   * Servis kaldır
   */
  static remove(name: string): boolean {
    return this.registry.delete(name);
  }

  /**
   * Tüm servisleri temizle
   */
  static clear(): void {
    this.registry.clear();
  }
}

// Initialize service registry with default services
ServiceRegistry.register('auth', authService);
ServiceRegistry.register('alarm', alarmService);
ServiceRegistry.register('unit', unitService);
ServiceRegistry.register('wagon', wagonService);
ServiceRegistry.register('fleet', fleetService);
ServiceRegistry.register('user', userService);
ServiceRegistry.register('tenant', tenantService);
ServiceRegistry.register('building', buildingService);
ServiceRegistry.register('notification', notificationService);

// ==================== SERVICE GROUPS ====================

/**
 * Domain-based service groups
 */
export const DomainServices = {
  // Authentication & Authorization
  Auth: authService,
  
  // Monitoring & Alerts
  Monitoring: {
    Alarms: alarmService,
    Notifications: notificationService
  },
  
  // Asset Management
  Assets: {
    Units: unitService,
    Wagons: wagonService,
    Fleets: fleetService
  },
  
  // System Management
  System: {
    Users: userService,
    Tenants: tenantService,
    Buildings: buildingService
  }
};

/**
 * Feature-based service groups
 */
export const FeatureServices = {
  // Dashboard features
  Dashboard: {
    getAlarms: alarmService.getAlarms.bind(alarmService),
    getUnitsRequiringAttention: unitService.getUnitsRequiringAttention.bind(unitService),
    getAlarmStatistics: alarmService.getAlarmStatistics.bind(alarmService)
  },
  
  // Unit details features
  UnitDetails: {
    getUnit: unitService.getUnit.bind(unitService),
    getUnitAlarms: unitService.getUnitAlarms.bind(unitService),
    getUnitPerformance: unitService.getUnitPerformance.bind(unitService),
    getUnitWagons: unitService.getUnitWagons.bind(unitService)
  },
  
  // Alarm management features
  AlarmManagement: {
    getAlarm: alarmService.getAlarm.bind(alarmService),
    acknowledgeAlarms: alarmService.acknowledgeAlarms.bind(alarmService),
    resolveAlarms: alarmService.resolveAlarms.bind(alarmService),
    getAlarmHistory: alarmService.getAlarmHistory.bind(alarmService)
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Servisleri initialize et (app başlangıcında çağırılabilir)
 */
export function initializeServices(): void {
  console.log('Initializing API services...');
  
  // Servis başlangıç işlemleri burada yapılabilir
  // Örneğin: session kontrolü, token refresh vs.
  
  // Auth service initialization
  authService.validateSession().catch(() => {
    console.log('No valid session found');
  });
  
  // Service registry initialized event
  const event = new CustomEvent('services-initialized', {
    detail: { timestamp: new Date().toISOString() }
  });
  globalThis.dispatchEvent(event);
}

/**
 * Servisleri temizle (logout veya app cleanup için)
 */
export function cleanupServices(): void {
  console.log('Cleaning up API services...');
  
  // Clear all caches
  ServiceFactory.clearAll();
  ServiceRegistry.clear();
  
  // Clear any ongoing streams or subscriptions
  alarmService.unsubscribeFromAlarms();
  
  // Service cleanup event
  const event = new CustomEvent('services-cleaned', {
    detail: { timestamp: new Date().toISOString() }
  });
  globalThis.dispatchEvent(event);
}

/**
 * Tüm servislerin health check'ini yap
 */
export async function checkServicesHealth(): Promise<Record<string, boolean>> {
  const healthResults: Record<string, boolean> = {};
  
  const services = [
    { name: 'auth', check: () => authService.validateSession() },
    { name: 'alarm', check: () => alarmService.getRecentAlarms(1) },
    { name: 'unit', check: () => unitService.getUnits({ limit: 1 }) },
    { name: 'wagon', check: () => wagonService.getWagons({ limit: 1 }) },
    { name: 'fleet', check: () => fleetService.getFleets({ limit: 1 }) }
  ];
  
  for (const service of services) {
    try {
      await service.check();
      healthResults[service.name] = true;
    } catch (error) {
      console.error(`${service.name} service health check failed:`, error);
      healthResults[service.name] = false;
    }
  }
  
  return healthResults;
}

/**
 * Service event listener'ı ekle
 */
export function onServiceEvent(
  eventName: 'services-initialized' | 'services-cleaned' | 'service-error',
  callback: (event: CustomEvent) => void
): () => void {
  const handler = (event: Event) => {
    callback(event as CustomEvent);
  };
  
  globalThis.addEventListener(eventName, handler);
  
  // Cleanup function
  return () => {
    globalThis.removeEventListener(eventName, handler);
  };
}

// ==================== TYPE RE-EXPORTS ====================

// Auth types
export type {
  LoginRequest,
  RegisterRequest,
} from '../types/requests/auth.types';

export type  {
  LoginResponse
} from '../types/responses/auth.types';

export type {
    UserProfile,
    UserRole
} from '../types/api.types';

export type {
    Alarm,
    AlarmStatistics
} from '../types/api.types';

export type {
    AlarmQueryParams
} from './alarm.service';
// Alarm types
export type {
  AlarmSeverity,
  AlarmStatus
} from '../types/requests/common.types';

// Unit types
export type {
  Unit,
  UnitType,
  UnitStatus,
  UnitPerformance
} from '../types/api.types';

export type {
    UnitQueryParams
} from '../types/requests/unit.types';

// Common types
export type {
  ApiResponse,
  PaginatedResponse,
  BaseQueryParams,
  ID,
  ExportFormat
} from '../types/api.types';

// ==================== DEFAULT EXPORT ====================

/**
 * Tüm servisleri içeren ana nesne
 */
const services = {
  // Instances
  auth: authService,
  alarm: alarmService,
  unit: unitService,
  wagon: wagonService,
  fleet: fleetService,
  user: userService,
  tenant: tenantService,
  building: buildingService,
  notification: notificationService,
  
  // Groups
  domains: DomainServices,
  features: FeatureServices,
  
  // Utility functions
  initialize: initializeServices,
  cleanup: cleanupServices,
  checkHealth: checkServicesHealth,
  onEvent: onServiceEvent,
  
  // Factory & Registry
  factory: ServiceFactory,
  registry: ServiceRegistry
};

export default services;

// ==================== GLOBAL TYPE EXTENSIONS ====================

declare global {
  interface Window {
    /**
     * Debug için - sadece development'ta kullanın
     */
    __SERVICES__?: typeof services;
  }
}

// Development için global erişim (sadece dev mode'da)
if (import.meta.env.DEV) {
  (globalThis as any).__SERVICES__ = services;
  console.log('API services registered globally as window.__SERVICES__');
}